import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { Subject, Observable, interval } from 'rxjs';
import { takeUntil, startWith, switchMap } from 'rxjs/operators';
import { Chart, ChartConfiguration, ChartType } from 'chart.js';

import { AuthService } from '../../../../services/auth.service';
import { UserActivityService } from '../../../../services/user-activity.service';
import { AuditService } from '../../../../services/audit.service';
import { User } from '../../../../models/user.model';
import {
  ActivityDashboardData,
  UserActivitySession,
  UserActivity,
  UserBehaviorAnalytics,
  SecurityEvent,
  EngagementLevel,
  ActivityType,
  DeviceType
} from '../../../../models/audit.model';

@Component({
  selector: 'app-user-activity-dashboard',
  templateUrl: './user-activity-dashboard.component.html',
  styleUrls: ['./user-activity-dashboard.component.css']
})
export class UserActivityDashboardComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();

  @ViewChild('hourlyActivityChart', { static: true }) hourlyActivityChart: ElementRef;
  @ViewChild('dailyTrendsChart', { static: true }) dailyTrendsChart: ElementRef;
  @ViewChild('deviceTypesChart', { static: true }) deviceTypesChart: ElementRef;
  @ViewChild('engagementChart', { static: true }) engagementChart: ElementRef;

  // Data
  currentUser: User;
  dashboardData: ActivityDashboardData = {};
  userAnalytics: UserBehaviorAnalytics | null = null;
  activeSessions: UserActivitySession[] = [];
  realtimeActivities: UserActivity[] = [];
  securityEvents: SecurityEvent[] = [];

  // Charts
  private hourlyChart: Chart;
  private dailyChart: Chart;
  private deviceChart: Chart;
  private engagementChartInstance: Chart;

  // Filters and state
  selectedTimeRange = '24h';
  selectedUser = 'all';
  loading = false;
  autoRefresh = true;
  refreshInterval = 30; // seconds

  // Date range
  dateRange = {
    start: new Date(Date.now() - 24 * 60 * 60 * 1000), // 24 hours ago
    end: new Date()
  };

  // Activity types for filtering
  activityTypes = Object.values(ActivityType);
  deviceTypes = Object.values(DeviceType);
  engagementLevels = Object.values(EngagementLevel);

  // UI state
  selectedTab = 'overview';
  showRealtime = true;
  selectedMetrics: string[] = ['sessions', 'activities', 'users', 'duration'];

  constructor(
    private authService: AuthService,
    private userActivityService: UserActivityService,
    private auditService: AuditService
  ) { }

  ngOnInit(): void {
    // Get current user
    this.authService.user$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.currentUser = user;
        if (user) {
          this.loadDashboardData();
          this.setupRealTimeUpdates();
        }
      });

    // Log dashboard access
    this.auditService.logNavigation('/admin/user-activity-dashboard', 'User Activity Dashboard');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    // Destroy charts
    if (this.hourlyChart) this.hourlyChart.destroy();
    if (this.dailyChart) this.dailyChart.destroy();
    if (this.deviceChart) this.deviceChart.destroy();
    if (this.engagementChartInstance) this.engagementChartInstance.destroy();
  }

  async loadDashboardData(): Promise<void> {
    this.loading = true;

    try {
      // Load dashboard summary data
      await this.loadSummaryData();

      // Load active sessions
      await this.loadActiveSessions();

      // Load user analytics
      await this.loadUserAnalytics();

      // Load security events
      await this.loadSecurityEvents();

      // Initialize charts
      setTimeout(() => {
        this.initializeCharts();
      }, 100);

    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      this.loading = false;
    }
  }

  private async loadSummaryData(): Promise<void> {
    // This would typically come from a service that aggregates data
    this.dashboardData = {
      summary: {
        active_users: 12,
        total_sessions: 156,
        total_activities: 2847,
        avg_session_duration: 28.5
      },
      real_time_activities: [],
      hourly_activity: this.generateMockHourlyData(),
      daily_trends: this.generateMockDailyTrends(),
      top_pages: [
        { page: '/dashboard', visits: 234, unique_visitors: 45 },
        { page: '/reports', visits: 189, unique_visitors: 38 },
        { page: '/zones', visits: 167, unique_visitors: 34 },
        { page: '/employees', visits: 143, unique_visitors: 29 },
        { page: '/settings', visits: 98, unique_visitors: 22 }
      ],
      user_engagement: [
        { user_email: 'admin@parkbot.com', engagement_score: 95, last_activity: new Date() },
        { user_email: 'manager@parkbot.com', engagement_score: 87, last_activity: new Date() },
        { user_email: 'operator@parkbot.com', engagement_score: 78, last_activity: new Date() }
      ],
      security_events: []
    };
  }

  private async loadActiveSessions(): Promise<void> {
    // Load current active sessions
    // This would be implemented in the user activity service
    this.activeSessions = [];
  }

  private async loadUserAnalytics(): Promise<void> {
    if (!this.currentUser) return;

    this.userAnalytics = await this.userActivityService.getUserBehaviorAnalytics(
      this.currentUser.uid,
      this.dateRange.start,
      this.dateRange.end
    );
  }

  private async loadSecurityEvents(): Promise<void> {
    // Load recent security events
    this.securityEvents = [];
  }

  private setupRealTimeUpdates(): void {
    if (!this.autoRefresh) return;

    // Subscribe to real-time activity stream
    this.userActivityService.getRealTimeActivityStream()
      .pipe(takeUntil(this.destroy$))
      .subscribe(activities => {
        this.realtimeActivities = activities.slice(0, 10); // Show last 10
      });

    // Auto-refresh data every 30 seconds
    interval(this.refreshInterval * 1000)
      .pipe(
        startWith(0),
        switchMap(() => this.loadDashboardData()),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private initializeCharts(): void {
    this.createHourlyActivityChart();
    this.createDailyTrendsChart();
    this.createDeviceTypesChart();
    this.createEngagementChart();
  }

  private createHourlyActivityChart(): void {
    if (!this.hourlyActivityChart || !this.dashboardData.hourly_activity) return;

    const ctx = this.hourlyActivityChart.nativeElement.getContext('2d');

    const config: ChartConfiguration = {
      type: 'line' as ChartType,
      data: {
        labels: this.dashboardData.hourly_activity.map(item => `${item.hour}:00`),
        datasets: [{
          label: 'Activity Count',
          data: this.dashboardData.hourly_activity.map(item => item.activity_count),
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.4,
          fill: true
        }]
      },
      options: {
        responsive: true,
        plugins: {
          title: {
            display: true,
            text: 'Hourly Activity Pattern (Last 24h)'
          }
        },
        scales: {
          y: {
            beginAtZero: true
          }
        }
      }
    };

    this.hourlyChart = new Chart(ctx, config);
  }

  private createDailyTrendsChart(): void {
    if (!this.dailyTrendsChart || !this.dashboardData.daily_trends) return;

    const ctx = this.dailyTrendsChart.nativeElement.getContext('2d');

    const config: ChartConfiguration = {
      type: 'bar' as ChartType,
      data: {
        labels: this.dashboardData.daily_trends.map(item => item.date),
        datasets: [
          {
            label: 'Sessions',
            data: this.dashboardData.daily_trends.map(item => item.sessions),
            backgroundColor: 'rgba(34, 197, 94, 0.8)'
          },
          {
            label: 'Activities',
            data: this.dashboardData.daily_trends.map(item => item.activities),
            backgroundColor: 'rgba(59, 130, 246, 0.8)'
          }
        ]
      },
      options: {
        responsive: true,
        plugins: {
          title: {
            display: true,
            text: 'Daily Activity Trends (Last 7 Days)'
          }
        },
        scales: {
          y: {
            beginAtZero: true
          }
        }
      }
    };

    this.dailyChart = new Chart(ctx, config);
  }

  private createDeviceTypesChart(): void {
    if (!this.deviceTypesChart) return;

    const ctx = this.deviceTypesChart.nativeElement.getContext('2d');

    // Mock device data
    const deviceData = [
      { type: 'Desktop', count: 145, percentage: 65 },
      { type: 'Mobile', count: 58, percentage: 26 },
      { type: 'Tablet', count: 20, percentage: 9 }
    ];

    const config: ChartConfiguration = {
      type: 'doughnut' as ChartType,
      data: {
        labels: deviceData.map(item => item.type),
        datasets: [{
          data: deviceData.map(item => item.count),
          backgroundColor: [
            'rgba(59, 130, 246, 0.8)',
            'rgba(34, 197, 94, 0.8)',
            'rgba(251, 191, 36, 0.8)'
          ]
        }]
      },
      options: {
        responsive: true,
        plugins: {
          title: {
            display: true,
            text: 'Device Types'
          }
        }
      }
    };

    this.deviceChart = new Chart(ctx, config);
  }

  private createEngagementChart(): void {
    if (!this.engagementChart || !this.dashboardData.user_engagement) return;

    const ctx = this.engagementChart.nativeElement.getContext('2d');

    const config: ChartConfiguration = {
      type: 'radar' as ChartType,
      data: {
        labels: this.dashboardData.user_engagement.map(user => user.user_email.split('@')[0]),
        datasets: [{
          label: 'Engagement Score',
          data: this.dashboardData.user_engagement.map(user => user.engagement_score),
          borderColor: 'rgb(139, 92, 246)',
          backgroundColor: 'rgba(139, 92, 246, 0.2)'
        }]
      },
      options: {
        responsive: true,
        plugins: {
          title: {
            display: true,
            text: 'User Engagement Levels'
          }
        },
        scales: {
          r: {
            beginAtZero: true,
            max: 100
          }
        }
      }
    };

    this.engagementChartInstance = new Chart(ctx, config);
  }

  // Event handlers
  onTimeRangeChange(range: string): void {
    this.selectedTimeRange = range;

    // Update date range based on selection
    const now = new Date();
    switch (range) {
      case '1h':
        this.dateRange.start = new Date(now.getTime() - 60 * 60 * 1000);
        break;
      case '24h':
        this.dateRange.start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case '7d':
        this.dateRange.start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '30d':
        this.dateRange.start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
    }

    this.dateRange.end = now;
    this.loadDashboardData();
  }

  onUserChange(userId: string): void {
    this.selectedUser = userId;
    this.loadDashboardData();
  }

  toggleAutoRefresh(): void {
    this.autoRefresh = !this.autoRefresh;

    if (this.autoRefresh) {
      this.setupRealTimeUpdates();
    }
  }

  refreshData(): void {
    this.loadDashboardData();
  }

  exportData(format: 'csv' | 'json' = 'csv'): void {
    // Export dashboard data
    const data = {
      summary: this.dashboardData.summary,
      timeRange: this.selectedTimeRange,
      exportTime: new Date().toISOString(),
      activeSessions: this.activeSessions.length,
      totalActivities: this.realtimeActivities.length
    };

    if (format === 'csv') {
      this.exportToCSV(data);
    } else {
      this.exportToJSON(data);
    }
  }

  private exportToCSV(data: any): void {
    const csvContent = this.convertToCSV(data);
    this.downloadFile(csvContent, `activity_dashboard_${new Date().toISOString().split('T')[0]}.csv`, 'text/csv');
  }

  private exportToJSON(data: any): void {
    const jsonContent = JSON.stringify(data, null, 2);
    this.downloadFile(jsonContent, `activity_dashboard_${new Date().toISOString().split('T')[0]}.json`, 'application/json');
  }

  private convertToCSV(data: any): string {
    // Simple CSV conversion for dashboard summary
    const headers = ['Metric', 'Value'];
    const rows = [
      ['Active Users', data.summary?.active_users || 0],
      ['Total Sessions', data.summary?.total_sessions || 0],
      ['Total Activities', data.summary?.total_activities || 0],
      ['Avg Session Duration (min)', data.summary?.avg_session_duration || 0]
    ];

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\\n');

    return csvContent;
  }

  private downloadFile(content: string, filename: string, contentType: string): void {
    const blob = new Blob([content], { type: contentType });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  // Utility methods
  formatDuration(minutes: number): string {
    if (minutes < 60) {
      return `${Math.round(minutes)}m`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  }

  formatNumber(num: number): string {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  }

  getEngagementColor(level: EngagementLevel): string {
    switch (level) {
      case EngagementLevel.VERY_HIGH:
        return 'text-green-600';
      case EngagementLevel.HIGH:
        return 'text-blue-600';
      case EngagementLevel.MEDIUM:
        return 'text-yellow-600';
      case EngagementLevel.LOW:
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  }

  getActivityTypeIcon(type: ActivityType): string {
    switch (type) {
      case ActivityType.CLICK:
        return '👆';
      case ActivityType.SCROLL:
        return '📜';
      case ActivityType.FORM_INPUT:
        return '✏️';
      case ActivityType.SEARCH:
        return '🔍';
      case ActivityType.DOWNLOAD:
        return '⬇️';
      case ActivityType.UPLOAD:
        return '⬆️';
      default:
        return '📋';
    }
  }

  private generateMockHourlyData(): Array<{hour: number, activity_count: number}> {
    const data = [];
    for (let i = 0; i < 24; i++) {
      // Generate realistic activity pattern (higher during work hours)
      let count;
      if (i >= 8 && i <= 18) {
        count = Math.floor(Math.random() * 50) + 30; // Work hours: 30-80
      } else if (i >= 19 && i <= 22) {
        count = Math.floor(Math.random() * 30) + 15; // Evening: 15-45
      } else {
        count = Math.floor(Math.random() * 15) + 2; // Night/early morning: 2-17
      }
      data.push({ hour: i, activity_count: count });
    }
    return data;
  }

  private generateMockDailyTrends(): Array<{date: string, sessions: number, activities: number}> {
    const data = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

      data.push({
        date: dateStr,
        sessions: Math.floor(Math.random() * 40) + 20, // 20-60 sessions
        activities: Math.floor(Math.random() * 500) + 200 // 200-700 activities
      });
    }
    return data;
  }

  trackByActivityId(index: number, activity: UserActivity): string {
    return activity.id || index.toString();
  }

  getPageVisitPercentage(visits: number): number {
    if (!this.dashboardData.top_pages || this.dashboardData.top_pages.length === 0) {
      return 0;
    }
    const maxVisits = this.dashboardData.top_pages[0]?.visits || 1;
    return (visits / maxVisits) * 100;
  }
}